import React, { useState } from "react";
import { FaChevronDown, FaFilter, FaSearch, FaTimes } from "react-icons/fa";

import {
  getCurrentSportSeasonLabel,
  getSportSeasonRangeLabel,
} from "../utils/statistiquesJudokaUtils";

function StatistiquesToolbar({
  filters,
  seasons = [],
  totalCount = 0,
  resultsCount = 0,
  activeFiltersCount = 0,
  onFilterChange,
  onReset,
}) {
  const [isOpen, setIsOpen] = useState(false);

  const currentSeason = getCurrentSportSeasonLabel();

  return (
    <div className="sticky top-[88px] z-30 rounded-[2rem] border border-slate-200 bg-white/95 p-4 shadow-xl shadow-slate-200/60 backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/95 dark:shadow-black/20 lg:top-[104px]">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm font-bold text-slate-950 dark:text-white">
            {resultsCount} compétition{resultsCount > 1 ? "s" : ""} analysée
            {resultsCount > 1 ? "s" : ""} / {totalCount}
          </p>

          <p className="mt-1 text-xs font-medium text-slate-500 dark:text-white/45">
            Saison actuelle : {currentSeason} ·{" "}
            {getSportSeasonRangeLabel(currentSeason)}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-2 text-sm font-bold text-white transition hover:-translate-y-0.5 dark:bg-white dark:text-slate-950"
        >
          <FaFilter />
          Filtres
          {activeFiltersCount > 0 && (
            <span className="rounded-full bg-orange-500 px-2 py-0.5 text-xs text-white">
              {activeFiltersCount}
            </span>
          )}
          <FaChevronDown
            className={`transition ${isOpen ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      <div className="mt-4">
        <label className="relative block">
          <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

          <input
            type="search"
            value={filters.search}
            onChange={(event) => onFilterChange("search", event.target.value)}
            placeholder="Rechercher une compétition, un lieu, une observation..."
            className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-bold text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-sky-300 focus:bg-white focus:ring-4 focus:ring-sky-100 dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:focus:border-sky-300/50 dark:focus:bg-slate-950 dark:focus:ring-sky-500/10"
          />
        </label>
      </div>

      {isOpen && (
        <div className="mt-4 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.03]">
          <div className="grid gap-4 md:grid-cols-3">
            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                Saison sportive
              </span>

              <select
                value={filters.season}
                onChange={(event) =>
                  onFilterChange("season", event.target.value)
                }
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-sky-300/50 dark:focus:ring-sky-500/10"
              >
                <option value="all">Toutes les saisons</option>
                <option value="current">
                  Saison actuelle ({currentSeason})
                </option>

                {seasons
                  .filter((season) => season !== currentSeason)
                  .map((season) => (
                    <option key={season} value={season}>
                      Saison {season}
                    </option>
                  ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                Statut
              </span>

              <select
                value={filters.status}
                onChange={(event) =>
                  onFilterChange("status", event.target.value)
                }
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-sky-300/50 dark:focus:ring-sky-500/10"
              >
                <option value="all">Toutes</option>
                <option value="completed">Compétitions effectuées</option>
                <option value="upcoming">Compétitions à venir</option>
                <option value="withResults">Avec résultat</option>
                <option value="withoutResults">Sans résultat</option>
                <option value="podium">Podiums</option>
              </select>
            </label>

            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                Tri
              </span>

              <select
                value={filters.sort}
                onChange={(event) => onFilterChange("sort", event.target.value)}
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-sky-300/50 dark:focus:ring-sky-500/10"
              >
                <option value="dateDesc">Plus récent</option>
                <option value="dateAsc">Plus ancien</option>
                <option value="withResultsFirst">Résultats d'abord</option>
                <option value="titleAsc">Nom A-Z</option>
              </select>
            </label>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={onReset}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:border-red-200 hover:text-red-600 dark:border-white/10 dark:bg-slate-950 dark:text-white"
            >
              <FaTimes />
              Réinitialiser
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default StatistiquesToolbar;
